import React, { useRef, useEffect, useCallback } from 'react';
import { useViewport } from '@atlassian/jira-software-deployments-common-table/src/components/table/common/context/viewport/context';
import { getPositionForTimestamp } from '@atlassian/jira-software-deployments-common-table/src/utils';
import { LoadingStateOptions } from '@atlassian/jira-software-deployments-service-query-types';
import { getTimelineDurationFromTimeWindowFilter } from '../../../common/utils/timeline-duration';
import { useTimeWindowFilter } from '../../../controllers/date-filter/utils';
import { useCoreData } from '../../../services/core-data';
import { useEarliestVisibleDeploymentDate } from '../../../services/core-data/main';
import { LoadingStatusIndicator } from './loading-status-indicator';

// Preload next deployments 150px before the bottom
const THRESHOLD = 150;

const useVerticalPagination = () => {
	const {
		serviceResult: { loadingState },
		getNextIssuePage,
	} = useCoreData();
	const { requestViewportScrollBounding } = useViewport();
	const lastScrollHeightBeforePaginated = useRef<unknown>(null);
	const lastScrollTop = useRef<unknown>(null);

	// Ensure we keep paginating until there's no more empty space
	// at the bottom of the table after the initial load. This is
	// possible with the AGS-powered backend because we don't
	// know exactly how many issues will be returned for a given page
	// of data.
	useEffect(() => {
		const scrollBounding = requestViewportScrollBounding();
		const isMoreFreeSpace = !scrollBounding.isOverflowY;
		if (
			loadingState === LoadingStateOptions.READY &&
			isMoreFreeSpace &&
			getNextIssuePage !== null
		) {
			getNextIssuePage();
		}
	}, [loadingState, getNextIssuePage, requestViewportScrollBounding]);

	const onScroll = useCallback(
		(target: Element) => {
			const isDownwardVerticallyScrolled =
				lastScrollTop.current === null
					? false
					: // @ts-expect-error - TS2571 - Object is of type 'unknown'.
						lastScrollTop.current < target.scrollTop;
			lastScrollTop.current = target.scrollTop;

			if (lastScrollHeightBeforePaginated.current !== null) {
				// it appears that you can keep scrolling a container past its max height.
				// In this example if you scroll fast to the bottom and keep scrolling
				// there is a big jump when the new deployments load in.
				// TODO this isn't perfect at all, we need to fix this.
				// Maybe setting overscroll-behavior: none will help?
				// @ts-expect-error - TS2322 - Type 'unknown' is not assignable to type 'number'.
				// eslint-disable-next-line no-param-reassign
				target.scrollTop = lastScrollHeightBeforePaginated.current;
				lastScrollHeightBeforePaginated.current = null;
				return;
			}

			const isVerticalEndReached =
				target.scrollTop + target.clientHeight + THRESHOLD >= target.scrollHeight &&
				isDownwardVerticallyScrolled;

			if (
				isVerticalEndReached &&
				getNextIssuePage !== null &&
				loadingState === LoadingStateOptions.READY
			) {
				// load more issues and deployments but remember the scrollHeight so we don't
				// exceed this in the next animation frame.
				lastScrollHeightBeforePaginated.current = target.scrollHeight;
				getNextIssuePage();
			}
		},
		[getNextIssuePage, loadingState],
	);

	return onScroll;
};

const useHorizontalPagination = () => {
	const {
		serviceResult: { loadingState },
		getNextIssuePage,
	} = useCoreData();
	const earliestVisibleDeploymentDate = useEarliestVisibleDeploymentDate();
	const { requestViewportScrollBounding } = useViewport();
	const timeWindowFilter = useTimeWindowFilter();
	const timelineDuration = getTimelineDurationFromTimeWindowFilter(timeWindowFilter);
	const earliestVisibleDeploymentPosition =
		earliestVisibleDeploymentDate == null
			? null
			: getPositionForTimestamp(earliestVisibleDeploymentDate, timelineDuration);

	const paginateIfNeeded = useCallback(() => {
		const scrollBounding = requestViewportScrollBounding();
		const currentScrollPosition = scrollBounding.scrollLeft / scrollBounding.scrollWidth;
		const hasScrolledPastEarliestDeployment =
			earliestVisibleDeploymentPosition != null &&
			currentScrollPosition <= earliestVisibleDeploymentPosition;
		if (
			loadingState === LoadingStateOptions.READY &&
			hasScrolledPastEarliestDeployment &&
			getNextIssuePage !== null
		) {
			getNextIssuePage();
		}
	}, [
		earliestVisibleDeploymentPosition,
		requestViewportScrollBounding,
		getNextIssuePage,
		loadingState,
	]);

	// We don't know how far back in time a given page of deployments will push
	// the `earliestVisibleDeploymentPosition`, so we may need to immediately
	// re-trigger pagination (note that `loadingState` is a dependency of `paginateIfNeeded`).
	useEffect(paginateIfNeeded, [paginateIfNeeded]);

	const onScroll = paginateIfNeeded;
	return onScroll;
};

const IssuePageFetcher = () => {
	const { listenToViewportScroll, stopListeningToViewportScroll } = useViewport();

	const onHorizontalScroll = useHorizontalPagination();
	const onVerticalScroll = useVerticalPagination();

	useEffect(() => {
		const onScroll = (event: Event) => {
			if (event.target instanceof Element) {
				// Doesn't actually matter if the scroll was horizontal, vertical, or both.
				// Let each handler determine what to do...
				onHorizontalScroll();
				onVerticalScroll(event.target);
			}
		};
		listenToViewportScroll(onScroll);
		return () => {
			stopListeningToViewportScroll(onScroll);
		};
	}, [listenToViewportScroll, stopListeningToViewportScroll, onHorizontalScroll, onVerticalScroll]);

	return <LoadingStatusIndicator />;
};

export default IssuePageFetcher;
